import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { dynamicImageUrl, getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { SeoTags } from '#src/components/SeoTags/index.ts';
import { getRequestOnServer } from '#src/functions/getRequestOnServer.ts';
import { CoursePage } from '#src/pages/CoursePage/index.ts';
import { ssrWhenNotAuthenticated } from '#src/utils/tanstackRouterUtil.ts';

export const Route = createFileRoute('/{-$locale}/course_/$courseId')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const { locale, courseId } = params;
    const request = getRequestOnServer();
    await queryClient.ensureQueryData(courseQueryOptions({ locale, request, courseId }));
  },
  ssr: ssrWhenNotAuthenticated
});

function RouteComponent() {
  const { locale, courseId } = Route.useParams();
  const intl = useIntl();

  const query = useSuspenseQuery(courseQueryOptions({ locale, courseId }));
  const course = query.data.data;

  return (
    <>
      <SeoTags
        description={course.description}
        imageUrl={
          course.images.length
            ? dynamicImageUrl(course.images[0].imageUrl, { width: 1200 })
            : undefined
        }
        title={intl.formatMessage(
          {
            id: 'head.class-details-page',
            defaultMessage: '{course} at {org} - Schedule & Reviews'
          },
          {
            course: course.name,
            org: course.organization?.name
          }
        )}
        useCurrentCanonicalPath
      />

      <CoursePage course={course} />
    </>
  );
}

function courseQueryOptions({
  locale,
  request,
  courseId
}: {
  locale?: string;
  request?: Request;
  courseId: string;
}) {
  return getReactQueryOptions(
    publicApiQueryClient.courses.get,
    { params: { id: courseId } },
    { context: { locale, request } }
  );
}
